import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import decision from '../services/DecisionService';
import { decision_slice_name, decision_thunks } from '../constants/decision';
import {
  getApplication,
  getApplicationWithDecisions,
} from './ApplicationSlice';
import { getDecisionApplicantIndexFromMeta } from './DecisionSelectors';
import { sortApplicantCovers } from '../utilities/application/coverHelper';

export const initialData = {
  loading: false,
  error: false,
  valid: false,
  quoteDecisions: null,
  productDecisions: null,
  editing: false,
  deletingApplicant: false,
};

export const postDecisionQuote = createAsyncThunk(
  decision_thunks.DECISION_POST_DECISION_QUOTE,
  async (applicationId, { rejectWithValue }) => {
    try {
      const response = await decision.postDecisionQuote(applicationId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getApplicantDecisionQuote = createAsyncThunk(
  decision_thunks.DECISION_GET_APPLICANT_DECISION_QUOTE,
  async (applicantId, { rejectWithValue }) => {
    try {
      const response = await decision.getApplicantDecisionQuote(applicantId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const postRemoveApplicantAtDecision = createAsyncThunk(
  decision_thunks.DECISION_POST_REMOVEAPPLICANTATDECISION,
  async ({ applicantId }, { rejectWithValue }) => {
    try {
      const response = await decision.postRemoveApplicantAtDecision(
        applicantId
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const decisionSlice = createSlice({
  name: decision_slice_name,
  initialState: initialData,
  reducers: {
    reset() {
      return initialData;
    },
    setInvalid(state) {
      state.valid = false;
    },
    setValid(state) {
      state.valid = true;
    },
    setEditing(state, action) {
      state.editing = action.payload;
    },
  },
  extraReducers: {
    [postDecisionQuote.pending]: (state) => {
      state.loading = true;
      state.valid = false;
    },
    [postDecisionQuote.fulfilled]: (state, action) => {
      const {
        data,
        data: { id },
      } = action.payload;
      state.id = id;
      state.loading = false;
      state.valid = true;
      state.quoteDecisions = {
        ...data,
        quoteApplicants: data.quoteApplicants.map((applicant) => ({
          ...applicant,
          quoteCovers: sortApplicantCovers(applicant.quoteCovers, true),
        })),
      };
    },
    [postDecisionQuote.rejected]: (state, action) => {
      state.error = action.error.message;
      state.loading = false;
      state.valid = false;
    },
    [getApplicantDecisionQuote.pending]: (state) => {
      state.loading = true;
    },
    [getApplicantDecisionQuote.fulfilled]: (state, action) => {
      const index = getDecisionApplicantIndexFromMeta(state, action);
      if (index >= 0) {
        state.quoteDecisions.quoteApplicants[index].quoteDecisionModel =
          action.payload.data;
      }
      state.loading = false;
    },
    [getApplicantDecisionQuote.rejected]: (state, action) => {
      state.error = action.error.message;
      state.loading = false;
    },
    [getApplication.pending]: (state) => {
      state.loading = true;
    },
    [getApplication.fulfilled]: (state, action) => {
      if (action.payload?.data?.quote) {
        state.quoteDecisions = {
          ...action.payload.data.quote,
          quoteApplicants: action.payload.data.quote.quoteApplicants.map(
            (applicant) => ({
              ...applicant,
              quoteCovers: sortApplicantCovers(applicant.quoteCovers, true),
            })
          ),
        };
        state.valid = true;
      }
      state.loading = false;
    },
    [getApplication.rejected]: (state) => {
      state.loading = false;
    },
    [getApplicationWithDecisions.pending]: (state) => {
      state.loading = true;
    },
    [getApplicationWithDecisions.fulfilled]: (state, action) => {
      if (action.payload.data.decisions) {
        state.productDecisions = action.payload.data.decisions;
        state.valid = true;
      }
      state.loading = false;
    },
    [getApplicationWithDecisions.rejected]: (state) => {
      state.loading = false;
    },
    [postRemoveApplicantAtDecision.pending]: (state) => {
      state.deletingApplicant = true;
    },
    [postRemoveApplicantAtDecision.fulfilled]: (state, action) => {
      const {
        data,
        data: { id },
      } = action.payload;
      state.id = id;
      state.valid = true;
      state.quoteDecisions = {
        ...data,
        quoteApplicants: data.quoteApplicants.map((applicant) => ({
          ...applicant,
          quoteCovers: sortApplicantCovers(applicant.quoteCovers, true),
        })),
      };
      state.deletingApplicant = false;
    },
    [postRemoveApplicantAtDecision.rejected]: (state) => {
      state.deletingApplicant = false;
    },
  },
});

export const { reset, setInvalid, setValid, setEditing } =
  decisionSlice.actions;

export default decisionSlice;
